import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import { Search, Plus, Minus, Trash2, User, Mail, Phone, MapPin, Building, Calendar, AlertTriangle, ShoppingCart, CreditCard, CheckCircle, Download, Receipt, Info, X, CalendarDaysIcon, MessageCircle, Package } from 'lucide-react';
import { addOrders, getBusiness, getPosItems, getShippingAddresses, deleteCart, getCategoryList } from "helpers/backend_helper";
import { getImageUrl } from "constants/constants";
import Select from 'react-select';
import { useCart, CartItem as CartItemType } from 'hooks/useCart';
import { useShare } from 'hooks/useShare';
import { AppContext } from "context/AppContext";
import { INVOICE_URL } from "constants/settings";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";
import Modal from 'Common/Components/Modal';
import { AddUser } from '../User/AddUser';

interface Business {
    id: number;
    businessName: string;
    gstNo: string;
    pan: string;
    ownerName: string;
    businessEstablishmentYear: number;
    businessType: string;
    addressLine1: string;
    addressLine2: string;
    landmark: string;
    city: string;
    district: string;
    state: string;
    country: string;
    pinCode: number;
    userId: number;
    userName: string;
    phone: string;
    email: string;
    status: string;
    isActive: boolean;
}

interface Category {
    id: number;
    name: string;
    logo?: string;
    backgroundColor?: string;
}

interface Product {
    imageUrl?: string;
    variantId: number;
    productId: number;
    sku: string;
    productName: string;
    brandName: string;
    categoryName: string;
    sizeNames: string;
    colors: string[];
    unitPrice: string;
    packOf: number;
    discountPercent: string;
    discountValue: string;
    inventory: number;
    image?: string;
}

interface CartItem extends Product {
    quantity: number;
}

interface Address {
    id: number;
    addressType: string;
    addressLine1: string;
    addressLine2: string;
    landmark: string;
    city: string;
    district: string;
    state: string;
    country: string;
    pinCode: string;

}

interface OrderDetails {
    id: number;
    orderId: string;
    orderDate: string;
    status: 'Processing' | 'Confirmed' | 'Shipped' | 'Delivered';
    items: CartItem[];
    shippingAddress: Address;
    businessDetails: Business;
    summary: {
        totalProducts: number;
        totalQuantity: number;
        subTotal: number;
        totalDiscount: number;
        gst: number;
        shipping: number;
        total: number;
    };
    filePath: string;
}

const POSNew = () => {
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<number>(0);
    const [selectedBusinessDetails, setSelectedBusinessDetails] = useState<Business | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [quantity, setQuantity] = useState<string>("1");
    const [products, setProducts] = useState<Product[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedBusinessOption, setSelectedBusinessOption] = useState<any>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [pendingProduct, setPendingProduct] = useState<Product | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [productSearchQuery, setProductSearchQuery] = useState<string>("");
    const [showCheckout, setShowCheckout] = useState<boolean>(false);
    const [checkoutStep, setCheckoutStep] = useState<number>(1);
    const [shippingCharge] = useState<number>(0);
    const [taxRate] = useState<number>(5);
    const [selectedAddress, setSelectedAddress] = useState<number | null>(null);
    const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSyncingCart, setIsSyncingCart] = useState<boolean>(false);
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [showBusinessDetails, setShowBusinessDetails] = useState<boolean>(false);
    const [isNextLoading, setIsNextLoading] = useState<boolean>(false);
    const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
    const [editingPriceValue, setEditingPriceValue] = useState<string>('');
    const [cartSuccessMessage, setCartSuccessMessage] = useState<string | null>(null);
    const [showUserModal, setShowUserModal] = useState<boolean>(false);
    const [userData, setUserData] = useState<any>();
    
    const { user } = useContext(AppContext);
    const commonHelper = new Common();
    const { shareOnWhatsApp } = useShare();

    const {
        cartItems,
        cartSummary,
        isLoading: isCartLoading,
        error: cartError,
        addItem,
        updateItem,
        updateItemPrice,
        removeItem,
        refreshCart,
        clearCart,
        syncCartWithBackend,
        calculateLocalSummary
    } = useCart(user?.userId ?? 0, selectedBusiness);

    const CURRENCY ="$";
    // Memoize local summary calculation to avoid recalculating on every render
    const localSummary = useMemo(() => calculateLocalSummary(), [cartItems, calculateLocalSummary]);

    const addToCartButtonRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const quantityInputRef = useRef<HTMLInputElement>(null);

    // Fetch businesses on component mount
    useEffect(() => {
        fetchBusinesses();
        fetchPosItems();
        fetchCategories();
        console.log(user);
    }, []);

    // Update useEffect to refresh cart when business changes
    useEffect(() => {
        if (selectedBusiness) {
            console.log("Refreshing cart:2");
            try{
                refreshCart();
            }
            catch(error){
                console.log(error);
            }
        }
    }, [selectedBusiness, refreshCart]);

    const loadShippingAddress = async () => {
        setIsLoading(true);

        try {
            const response = await getShippingAddresses(selectedBusiness);
            if (response.data && response.data.length > 0) {
                const firstAddress = response.data[0];
                setSelectedAddress(firstAddress.id);
                setAddresses(response.data);
                console.log('Shipping addresses loaded:', response.data);
            } else {
                setAddresses([]);
                setSelectedAddress(null);
                console.log('No shipping addresses found');
            }
        } catch (error) {
            console.error('Error loading shipping addresses:', error);
            setAddresses([]);
            setSelectedAddress(null);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchBusinesses = async () => {
        try {
            setIsLoading(true);
            const response = await getBusiness('Verified');
            if (response?.data) {
                setBusinesses(response.data);
            } else {
                setBusinesses([]);
            }
        } catch (error) {
            console.error("Error fetching businesses:", error);
            commonHelper.showErrorMessage("Failed to load businesses");
            setBusinesses([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPosItems = async () => {
        try {
            setIsLoading(true);
            const response = await getPosItems();
            if (response?.data) {
                setProducts(response.data);
            } else {
                setProducts([]);
            }
        } catch (error) {
            console.error("Error fetching POS items:", error);
            commonHelper.showErrorMessage("Failed to load products");
            setProducts([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await getCategoryList();
            if (response?.success && response?.data) {
                setCategories(response.data);
                // Auto-select first category if available
                if (response.data.length > 0 && !selectedCategory) {
                    setSelectedCategory(response.data[0].id);
                }
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            commonHelper.showErrorMessage("Failed to load categories");
            setCategories([]);
        }
    };

    // Filter products by selected category
    const filteredProducts = useMemo(() => {
        let filtered = products;
        
        // Filter by category if one is selected
        if (selectedCategory !== null) {
            const category = categories.find(cat => cat.id === selectedCategory);
            if (category) {
                filtered = filtered.filter(product => product.categoryName === category.name);
            }
        }
        
        // Filter by search query if present (minimum 2 characters)
        if (productSearchQuery.trim().length >= 2) {
            const query = productSearchQuery.toLowerCase();
            filtered = filtered.filter(product => 
                product.productName.toLowerCase().includes(query) ||
                product.sku.toLowerCase().includes(query)
            );
        }
        
        return filtered;
    }, [products, selectedCategory, categories, productSearchQuery]);

    const handleAddProductToCart = (product: Product) => {
        console.log("handleAddProductToCart called", { product, selectedBusiness });
        
        if (!selectedBusiness) {
            commonHelper.showErrorMessage("Please select a customer first");
            return;
        }

        if (product.inventory === 0) {
            setPendingProduct(product);
            setShowConfirmDialog(true);
            return;
        }

        try {
            // Check if item already exists in cart
            const existingItem = cartItems.find(item => item.variantId === product.variantId);
            console.log("Existing item in cart:", existingItem);
            
            // addItem handles incrementing quantity if item already exists in cart
            addItem(product);
            console.log("addItem called, cart items should update");
            
            // Show success message in customer detail panel
            if (existingItem) {
                setCartSuccessMessage(`${product.productName} quantity updated`);
            } else {
                setCartSuccessMessage(`${product.productName} added to cart`);
            }
        } catch (error) {
            console.error("Error adding product:", error);
            commonHelper.showErrorMessage("Failed to add product to cart");
        }
    };

    // Auto-hide success message after 3 seconds
    useEffect(() => {
        if (cartSuccessMessage) {
            const timer = setTimeout(() => {
                setCartSuccessMessage(null);
            }, 3000);
            
            return () => clearTimeout(timer);
        }
    }, [cartSuccessMessage]);

    const handleSearch = async () => {
        if(!selectedBusiness){
            commonHelper.showErrorMessage("Please select a customer");
            return;
        }
        else if(searchQuery.trim().length <= 2){
            commonHelper.showErrorMessage("Please enter a valid sku#");
            return;
        }
        setIsSearching(true);
        try {
            const foundProduct = products.find(product =>
                product?.sku?.toUpperCase() === searchQuery.toUpperCase()
            );

                    if (foundProduct) {
            const quantityToAdd = parseInt(quantity) || 1;
            // Add the product with the specified quantity
            for (let i = 0; i < quantityToAdd; i++) {
                await addItem(foundProduct);
            }
            setSearchQuery('');
            setQuantity('1');
            // Focus back to the SKU scan textbox
            searchInputRef.current?.focus();
        } else {
            commonHelper.showErrorMessage("Product not found with SKU: " + searchQuery);
            // Focus back to the SKU scan textbox even if product not found
            searchInputRef.current?.focus();
        }
        } catch (error) {
            console.error("Error searching products:", error);
            commonHelper.showErrorMessage("Error searching products");
        } finally {
            setIsSearching(false);
        }
    };

    const handleConfirmAdd = async () => {
        if (pendingProduct) {
            try {
                await addItem(pendingProduct);
                setPendingProduct(null);
                setShowConfirmDialog(false);
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 100);
            } catch (error) {
                console.error("Error adding product:", error);
                commonHelper.showErrorMessage("Failed to add product to cart");
            }
        }
    };

    const handleCancelAdd = () => {
        setPendingProduct(null);
        setShowConfirmDialog(false);
    };

    const updateQuantity = async (productId: number, increment: boolean) => {
        const item = cartItems.find(item => item.variantId === productId);
        if (!item) return;

        const newQuantity = increment ? item.quantity + 1 : item.quantity - 1;
        if (newQuantity > 0) {
            try {
                await updateItem(productId, newQuantity);
            } catch (error) {
                console.error("Error updating quantity:", error);
                commonHelper.showErrorMessage("Failed to update quantity");
            }
        } else {
            try {
                await removeItem(productId);
            } catch (error) {
                console.error("Error removing item:", error);
                commonHelper.showErrorMessage("Failed to remove item");
            }
        }
    };

    const removeFromCart = async (variantId: number) => {
        try {
            const item = cartItems.find(item => item.variantId === variantId);
            if (item?.id && item.id > 0) {
                // If item has an ID > 0, delete from API first
                await deleteCart({ id: item.id });
            }
            // Remove from local cart state
            await removeItem(variantId);
        } catch (error) {
            console.error("Error removing item:", error);
            commonHelper.showErrorMessage("Failed to remove item");
        }
    };

    const handlePriceEdit = (variantId: number, currentPrice: string) => {
        setEditingPriceId(variantId);
        setEditingPriceValue(currentPrice);
    };

    const handlePriceSave = async (variantId: number) => {
        try {
            const newPrice = parseFloat(editingPriceValue);
            if (isNaN(newPrice) || newPrice < 0) {
                commonHelper.showErrorMessage("Please enter a valid price");
                return;
            }
            
            await updateItemPrice(variantId, editingPriceValue);
            setEditingPriceId(null);
            setEditingPriceValue('');
        } catch (error) {
            console.error("Error updating price:", error);
            commonHelper.showErrorMessage("Failed to update price");
        }
    };

    const handlePriceCancel = () => {
        setEditingPriceId(null);
        setEditingPriceValue('');
    };

    const handleCheckout = async () => {
        if (cartItems.length === 0) {
            
            commonHelper.showErrorMessage("Cart is empty. Add items to proceed with checkout.");
            return;
        }

        setIsSyncingCart(true);
        try {
            await syncCartWithBackend();
            setShowCheckout(true);
            setCheckoutStep(2);
            loadShippingAddress();
        } catch (error) {
            console.error("Error syncing cart:", error);
            commonHelper.showErrorMessage("Failed to sync cart with backend");
        } finally {
            setIsSyncingCart(false);
        }
    };

    const handleNextStep = async () => {
        if(checkoutStep === 1){
            setIsNextLoading(true);
            try {
                await handleCheckout();
            } finally {
                setIsNextLoading(false);
            }
        }
        else if (checkoutStep < 3) {
            setCheckoutStep(checkoutStep + 1);
        }
    };

    const handlePrevStep = () => {
        if (checkoutStep > 1) {
            setCheckoutStep(checkoutStep - 1);
        }
    };

    const handleSubmitOrder = async () => {
        if (!selectedAddress || !selectedBusinessDetails) return;
        
        setIsSubmitting(true);
        try {
            const data = {
                userId: user?.userId ?? 0,
                businessId: selectedBusiness,
                shippingAddressId: selectedAddress,
                couponCode: '',
            }
            console.log(data);
            const response = await addOrders(data);
            console.log(response);

            const order: OrderDetails = {
                id: response.data.id,
                orderId: response.data.orderNo,
                orderDate: new Date().toISOString(),
                status: 'Confirmed',
                items: cartItems,
                shippingAddress: addresses.find(a => a.id === selectedAddress)!,
                businessDetails: selectedBusinessDetails,
                summary: cartSummary??{totalProducts:0,totalQuantity:0,subTotal:0,totalDiscount:0,gst:0,shipping:0,total:0},
                filePath: response.data.filePath
            };
            
            setOrderDetails(order);
            setCheckoutStep(3);
            
            // Clear cart and reset state
            clearCart();
            setSelectedAddress(null);
        } catch (error) {
            console.error("Error placing order:", error);
            commonHelper.showErrorMessage("Failed to place order. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownloadInvoice = () => {
        if (orderDetails?.filePath) {
            const invoiceUrl = `${INVOICE_URL}${orderDetails.filePath}`;
            console.log('Downloading invoice from:', invoiceUrl);
            window.open(invoiceUrl, '_blank');
        }
    };

    const handleViewOrderDetails = () => {
        if (orderDetails?.orderId) {
            window.open(`/order-detail/${orderDetails.id}`, '_blank');
        }
    };

    const handleShareInvoiceOnWhatsApp = () => {
        if (orderDetails?.filePath && selectedBusinessDetails) {
            const invoiceUrl = `${INVOICE_URL}${orderDetails.filePath}`;
            const customerPhone = selectedBusinessDetails.phone;
            
            const message = `Hi ${selectedBusinessDetails.ownerName},

Your order has been confirmed! 📋

Order Details:
🛒 Order ID: ${orderDetails.orderId}
📅 Order Date: ${new Date(orderDetails.orderDate).toLocaleDateString()}
💰 Total Amount: {CURRENCY}${orderDetails.summary.total.toFixed(2)}
🏢 Business: ${selectedBusinessDetails.businessName}

📄 Invoice: ${invoiceUrl}

Thank you for your business! 🙏`;
            
            shareOnWhatsApp(customerPhone, message);
        }
    };

    const handleNewOrder = () => {
        setShowCheckout(false);
        setCheckoutStep(1);
        clearCart();
        setSelectedAddress(null);
        setOrderDetails(null);
    };

    // Update business options for Select component
    const businessOptions = businesses.map(business => ({
        value: business.id,
        label: business.businessName
    }));

    // Update business change handler
    const handleBusinessChange = async (selectedOption: any) => {
        // Clear cart first
        clearCart();
        // Clear success message when business changes
        setCartSuccessMessage(null);

        setSelectedBusinessOption(selectedOption);
        const businessId = selectedOption?.value || 0;
        setSelectedBusiness(businessId);
        const business = businesses.find(b => b.id === businessId);
        setSelectedBusinessDetails(business || null);
   };

    // Toggle user modal
    const toggleUserModal = useCallback(() => {
        setShowUserModal(!showUserModal);
        if (showUserModal) {
            setUserData(null);
        }
    }, [showUserModal]);

    // Handle add customer button click
    const handleAddCustomer = () => {
        setUserData({});
        toggleUserModal();
    };

    // Handle user save
    const handleUserSave = async (result: any) => {
        console.log("User saved:", result);
        // User saved, but business form might be shown next (for customer role)
        // Don't close modal or refresh yet
    };

    // Handle business save (called after user with customer role creates business)
    const handleBusinessSave = async (result: any) => {
        console.log("Business saved:", result);
        try {
            // Refresh businesses list
            const response = await getBusiness('Verified');
            if (response?.data) {
                setBusinesses(response.data);
                
                // Find and select the newly created business
                const newBusiness = response.data.find((b: Business) => 
                    b.businessName === result.businessName || 
                    b.id === result.id
                );
                
                if (newBusiness) {
                    const newOption = {
                        value: newBusiness.id,
                        label: newBusiness.businessName
                    };
                    setSelectedBusinessOption(newOption);
                    setSelectedBusiness(newBusiness.id);
                    setSelectedBusinessDetails(newBusiness);
                    commonHelper.showSuccessMessage("Customer added and selected successfully!");
                }
            }
        } catch (error) {
            console.error("Error refreshing businesses:", error);
        } finally {
            toggleUserModal();
        }
    };

    // Add keyboard event handler for the dialog
    useEffect(() => {
        const handleDialogKeyPress = (e: KeyboardEvent) => {
            if (showConfirmDialog && e.key === 'Enter') {
                e.preventDefault();
                handleConfirmAdd();
            }
        };

        if (showConfirmDialog) {
            document.addEventListener('keypress', handleDialogKeyPress);
            setTimeout(() => {
                addToCartButtonRef.current?.focus();
            }, 100);
        }

        return () => {
            document.removeEventListener('keypress', handleDialogKeyPress);
        };
    }, [showConfirmDialog]);

    // Add keyboard event handler for price editing
    useEffect(() => {
        const handlePriceKeyPress = (e: KeyboardEvent) => {
            if (editingPriceId && e.key === 'Escape') {
                e.preventDefault();
                handlePriceCancel();
            }
        };

        if (editingPriceId) {
            document.addEventListener('keydown', handlePriceKeyPress);
        }

        return () => {
            document.removeEventListener('keydown', handlePriceKeyPress);
        };
    }, [editingPriceId]);

    return (
        <React.Fragment>
        <ToastContainer closeButton={false} limit={1} />

            <div className="w-full px-1 mt-5">
                {/* Zero Inventory Confirmation Dialog */}
                {showConfirmDialog && pendingProduct && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-zink-700 rounded-lg p-6 max-w-md w-full mx-4">
                            <div className="flex items-center gap-3 mb-4">
                                <AlertTriangle className="h-6 w-6 text-yellow-500" />
                                <h3 className="text-lg font-semibold">Zero Inventory Warning</h3>
                            </div>
                            <p className="text-slate-600 dark:text-zink-200 mb-4">
                                This product ({pendingProduct.sku}) has zero inventory. Are you sure you want to add it to the cart?
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    className="btn bg-slate-200 hover:bg-slate-300 text-slate-700"
                                    onClick={handleCancelAdd}
                                >
                                    Cancel
                                </button>
                                <button
                                    ref={addToCartButtonRef}
                                    className="btn bg-custom-500 text-white focus:ring-2 focus:ring-custom-500 focus:ring-offset-2"
                                    onClick={handleConfirmAdd}
                                    autoFocus
                                >
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4 mb-3">
                    {/* Business Selection and Product Search */}
                    <div className="card mb-0">
                        <div className="card-body p-0" style={{paddingTop: '5px', paddingBottom: '5px'}}>
                            <div className="flex items-center gap-4">
                                {/* Business Selection */}
                                <div className="flex-1 flex items-center gap-4">
                                    <div className="flex items-center gap-1">
                                        <span className="text-sm font-medium text-slate-600 dark:text-zink-200">Customer:</span>
                                        <div className="flex-1 min-w-[200px]">
                                            <Select
                                                 className="text-sm"
                                                classNamePrefix="select2-selection"
                                                placeholder="Select Business"
                                                options={businessOptions}
                                                value={selectedBusinessOption}
                                                onChange={handleBusinessChange}
                                                isDisabled={isLoading}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm bg-custom-500 text-white hover:bg-custom-600 p-2 flex items-center gap-1"
                                            onClick={handleAddCustomer}
                                            title="Add New Customer"
                                        >
                                            <Plus className="h-4 w-4" />
                                            <span>Add Customer</span>
                                        </button>
                                    </div>
                                    {selectedBusinessDetails ? (
                                        <div className="flex items-center gap-6 text-sm relative">
                                            <div className="flex items-center gap-2">
                                                
                                                <User className="h-4 w-4 text-custom-500" />
                                                
                                                    <span className="font-medium">{selectedBusinessDetails.ownerName}</span>
                                                    <CalendarDaysIcon className="h-4 w-4 text-custom-500" />
                                            
                                                    <span className="text-slate-500 ml-2">Est. {selectedBusinessDetails.businessEstablishmentYear}</span>
                                                
                                                <MapPin className="h-4 w-4 text-custom-500" />
                                                
                                                    <span>{selectedBusinessDetails.city}, {selectedBusinessDetails.state}</span>
                                            
                                            </div>
                                            
                                            <button
                                                className="btn btn-sm bg-slate-200 hover:bg-slate-300 p-1"
                                                onClick={() => setShowBusinessDetails(true)}
                                            >
                                                <Info className="h-4 w-4" />
                                            </button>
                                            
                                            {cartSuccessMessage && (
                                                <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg text-sm text-green-700 dark:text-green-300 shadow-lg z-10 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="h-4 w-4 flex-shrink-0" />
                                                        <span>{cartSuccessMessage}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-sm">
                                            <AlertTriangle className="h-5 w-5 text-red-500" />
                                            <span className="text-red-500 font-semibold">Please select a customer to continue</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Business Details Popup */}
                    {showBusinessDetails && selectedBusinessDetails && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-zink-700 rounded-lg p-6 max-w-2xl w-full mx-4">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-lg font-semibold">Business Details</h3>
                                    <button
                                        className="btn btn-sm bg-slate-200 hover:bg-slate-300 p-1"
                                        onClick={() => setShowBusinessDetails(false)}
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm text-slate-500">Business Name</label>
                                            <p className="font-medium">{selectedBusinessDetails.businessName}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Owner Name</label>
                                            <p className="font-medium">{selectedBusinessDetails.ownerName}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Business Type</label>
                                            <p className="font-medium">{selectedBusinessDetails.businessType}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Established Year</label>
                                            <p className="font-medium">{selectedBusinessDetails.businessEstablishmentYear}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm text-slate-500">Contact Information</label>
                                            <p className="font-medium">+91 {selectedBusinessDetails.phone}</p>
                                            <p className="font-medium">{selectedBusinessDetails.email}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Tax Information</label>
                                            <p className="font-medium">GST: {selectedBusinessDetails.gstNo}</p>
                                            <p className="font-medium">PAN: {selectedBusinessDetails.pan}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm text-slate-500">Address</label>
                                            <p className="font-medium">
                                                {selectedBusinessDetails.addressLine1}, {selectedBusinessDetails.addressLine2}
                                                {selectedBusinessDetails.landmark && `, ${selectedBusinessDetails.landmark}`}
                                            </p>
                                            <p className="font-medium">
                                                {selectedBusinessDetails.city}, {selectedBusinessDetails.district}, {selectedBusinessDetails.state} - {selectedBusinessDetails.pinCode}
                                            </p>
                                            <p className="font-medium">{selectedBusinessDetails.country}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cart and Checkout Wizard */}
                    <div className="bg-white dark:bg-zink-700 rounded-lg p-4">
                        <div className="grid grid-cols-12 gap-4">
                            {/* Wizard Content */}
                            <div className="col-span-9">
                                {/* Wizard Steps */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className={`flex items-center ${checkoutStep >= 1 ? (checkoutStep === 1 ? 'text-green-500 font-bold' : 'text-custom-500') : 'text-slate-400'}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${checkoutStep >= 1 ? (checkoutStep === 1 ? 'border-green-500' : 'border-custom-500') : 'border-slate-400'}`}>
                                            1
                                        </div>
                                        <span className="ml-2">Cart Items</span>
                                    </div>
                                    <div className="flex-1 h-0.5 mx-4 bg-slate-200"></div>
                                    <div className={`flex items-center ${checkoutStep >= 2 ? (checkoutStep === 2 ? 'text-green-500 font-bold' : 'text-custom-500') : 'text-slate-400'}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${checkoutStep >= 2 ? (checkoutStep === 2 ? 'border-green-500' : 'border-custom-500') : 'border-slate-400'}`}>
                                            2
                                        </div>
                                        <span className="ml-2">Order Summary</span>
                                    </div>
                                    <div className="flex-1 h-0.5 mx-4 bg-slate-200"></div>
                                    <div className={`flex items-center ${checkoutStep >= 3 ? (checkoutStep === 3 ? 'text-green-500 font-bold' : 'text-custom-500') : 'text-slate-400'}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${checkoutStep >= 3 ? (checkoutStep === 3 ? 'border-green-500' : 'border-custom-500') : 'border-slate-400'}`}>
                                            3
                                        </div>
                                        <span className="ml-2">Order Confirmation</span>
                                    </div>
                                </div>

                                {/* Step Content */}
                                <div className="h-[calc(100vh-280px)] overflow-hidden flex flex-col">
                                    {checkoutStep === 1 && (
                                        <div className="grid grid-cols-12 gap-4 h-full min-h-0">
                                            {/* Left Sidebar - Categories */}
                                            <div className="col-span-3 bg-slate-50 dark:bg-zink-600 rounded-lg p-3 flex flex-col h-full min-h-0">
                                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200 dark:border-zink-500 flex-shrink-0">
                                                    <ShoppingCart className="h-5 w-5 text-custom-500" />
                                                    <h6 className="text-sm font-semibold">Categories</h6>
                                                </div>
                                                <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
                                                    <button
                                                        onClick={() => setSelectedCategory(null)}
                                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                                            selectedCategory === null
                                                                ? 'bg-custom-500 text-white border-l-4 border-custom-600'
                                                                : 'bg-white dark:bg-zink-700 text-slate-700 dark:text-zink-200 hover:bg-slate-100 dark:hover:bg-zink-600 border-l-4 border-transparent'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Package className="h-4 w-4" />
                                                            <span>All Products</span>
                                                        </div>
                                                    </button>
                                                    {categories.slice(0, 5).map((category) => (
                                                        <button
                                                            key={category.id}
                                                            onClick={() => setSelectedCategory(category.id)}
                                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                                                selectedCategory === category.id
                                                                    ? 'bg-custom-400 text-white border-l-8 border-custom-600'
                                                                    : 'text-slate-700 dark:text-zink-200 hover:opacity-80 border-l-4 border-transparent'
                                                            }`}
                                                            style={selectedCategory !== category.id && category.backgroundColor ? {
                                                                backgroundColor: category.backgroundColor
                                                            } : undefined}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {category.logo ? (
                                                                    <img
                                                                        src={getImageUrl(category.logo, 'categoryThumbnail')}
                                                                        alt={category.name}
                                                                        className="w-6 h-6 rounded object-cover flex-shrink-0"
                                                                    />
                                                                ) : (
                                                                    <Package className="h-4 w-4 flex-shrink-0" />
                                                                )}
                                                                <span className="truncate font-bold">{category.name}</span>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Middle Panel - Product Grid */}
                                            <div className="col-span-9 flex flex-col h-full min-h-0">
                                                {/* Search Bar */}
                                                <div className="mb-4 flex-shrink-0">
                                                    <div className="relative">
                                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                        <input
                                                            type="text"
                                                            className="form-input pl-10 w-full"
                                                            placeholder="Search by name or SKU (min 2 characters)"
                                                            value={productSearchQuery}
                                                            onChange={(e) => setProductSearchQuery(e.target.value)}
                                                            minLength={0}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Product Grid */}
                                                {isLoading ? (
                                                    <div className="flex items-center justify-center flex-1">
                                                        <div className="animate-spin h-8 w-8 border-2 border-custom-500 border-t-transparent rounded-full"></div>
                                                    </div>
                                                ) : filteredProducts.length === 0 ? (
                                                    <div className="text-center py-12 text-slate-500 flex-1 flex items-center justify-center">
                                                        <div>
                                                            <Package className="h-12 w-12 mx-auto mb-3 text-slate-400" />
                                                            <p>No products found</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex-1 overflow-y-auto min-h-0">
                                                        <div className="grid grid-cols-4 gap-4">
                                                            {filteredProducts.map((product) => {
                                                            const unitPrice = parseFloat(product.unitPrice || '0');
                                                            const discountValue = parseFloat(product.discountValue || '0');
                                                            const netPrice = unitPrice - discountValue;
                                                            
                                                            return (
                                                                <div
                                                                    key={`product-${product.variantId}`}
                                                                    className="bg-white dark:bg-zink-700 rounded-lg border border-slate-200 dark:border-zink-500 hover:shadow-md transition-shadow overflow-hidden"
                                                                >
                                                                    <div className="relative">
                                                                        <div className="aspect-square bg-slate-100 dark:bg-zink-600 flex items-center justify-center">
                                                                            {product.imageUrl ? (
                                                                                <img
                                                                                    src={getImageUrl(product.imageUrl)}
                                                                                    alt={product.productName}
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            ) : (
                                                                                // <Package className="h-16 w-16 text-slate-400" />
                                                                                <img
                                                                                    src={"https://imgdb.plentima.com/arolisa/products/t/95a4c4b3-ad2e-4188-9f20-d4fcfdef6fba.webp"}
                                                                                    alt={product.productName}
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        {product.inventory === 0 && (
                                                                            <div className="absolute top-2 right-2">
                                                                                <span className="px-2 py-1 text-xs font-medium bg-red-500 text-white rounded">
                                                                                    Out of Stock
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="p-3">
                                                                        <h6 className="text-sm font-semibold mb-1 truncate" title={product.productName}>
                                                                            {product.productName}
                                                                        </h6>
                                                                       
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <div>
                                                                                <span className="text-sm font-bold text-custom-500">
                                                                                    {CURRENCY}{netPrice.toFixed(2)}
                                                                                </span>
                                                                                {discountValue > 0 && (
                                                                                    <span className="text-xs text-slate-400 line-through ml-2">
                                                                                        {CURRENCY}{unitPrice.toFixed(2)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <span className={`text-xs ${product.inventory > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                                                Stock: {product.inventory}
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            className="btn btn-sm bg-custom-500 text-white w-full flex items-center justify-center gap-1"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                handleAddProductToCart(product);
                                                                            }}
                                                                            disabled={!selectedBusiness}
                                                                        >
                                                                            <Plus className="h-4 w-4 flex-shrink-0" />
                                                                            <span className="whitespace-nowrap">Add to Cart</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {checkoutStep === 2 && (
                                        <div className="h-full overflow-y-auto">
                                            {/* Shipping Address Selection */}
                                            <div className="mb-6">
                                                <h4 className="text-sm font-medium mb-3">Select Shipping Address</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {addresses.map((address) => (
                                                        <div
                                                            key={`address-${address.id}`}
                                                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                                                                selectedAddress === address.id
                                                                    ? 'border-custom-500 bg-custom-50 dark:bg-custom-900/20'
                                                                    : 'border-slate-200 dark:border-zink-500 hover:border-custom-500'
                                                            }`}
                                                            onClick={() => setSelectedAddress(address.id)}
                                                        >
                                                            <div className="flex items-start justify-between mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                                                                        address.addressType === 'Billing'
                                                                            ? 'bg-blue-100 text-blue-600'
                                                                            : 'bg-purple-100 text-purple-600'
                                                                    }`}>
                                                                        {address.addressType}
                                                                    </span>
                                                                </div>
                                                                {selectedAddress === address.id && (
                                                                    <div className="w-5 h-5 rounded-full border-2 border-custom-500 flex items-center justify-center">
                                                                        <div className="w-3 h-3 rounded-full bg-custom-500"></div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="space-y-1 text-sm">
                                                                <p className="font-medium">{address.addressLine1}</p>
                                                                {address.addressLine2 && <p>{address.addressLine2}</p>}
                                                                {address.landmark && <p className="text-slate-500">Landmark: {address.landmark}</p>}
                                                                <p>{address.city}, {address.district}</p>
                                                                <p>{address.state}, {address.country} - {address.pinCode}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {!selectedAddress && (
                                                    <div className="mt-4 text-red-500 text-sm">
                                                        Please select a shipping address to continue
                                                    </div>
                                                )}
                                            </div>

     
                                        </div>
                                    )}

                                    {checkoutStep === 3 && orderDetails && (
                                        <div className="text-center py-8">
                                            <div className="mb-6">
                                                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                                <h3 className="text-lg font-semibold mb-2">Order Placed Successfully!</h3>
                                                <p className="text-slate-500 mb-4">
                                                    Your order has been confirmed and is being processed.
                                                </p>
                                                <div className="bg-slate-50 dark:bg-zink-600 rounded-lg p-4 inline-block">
                                                    <div className="text-sm text-slate-500 mb-1">Order Number</div>
                                                    <div className="text-2xl font-bold text-custom-500">
                                                        {orderDetails.orderId}
                                                    </div>
                                                    <div className="text-sm text-slate-500 mt-1">
                                                        {new Date(orderDetails.orderDate).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                                <button
                                                    className="btn bg-custom-500 text-white inline-flex items-center"
                                                    onClick={handleViewOrderDetails}
                                                >
                                                    <Receipt className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    <span>View Order Details</span>
                                                </button>
                                                <button
                                                    className="btn bg-green-500 text-white inline-flex items-center"
                                                    onClick={handleDownloadInvoice}
                                                >
                                                    <Download className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    <span>Download Invoice</span>
                                                </button>
                                                <button
                                                    className="btn bg-green-600 text-white inline-flex items-center"
                                                    onClick={handleShareInvoiceOnWhatsApp}
                                                    title="Share invoice on WhatsApp"
                                                >
                                                    <MessageCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    <span>Share on WhatsApp</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Cart Summary */}
                            <div className="col-span-3">
                                <div className="bg-slate-50 dark:bg-zink-600 rounded-lg p-4 sticky top-4 h-[calc(100vh-280px)] flex flex-col">
                                    <h6 className="text-15 font-medium mb-2 flex-shrink-0">Cart Summary</h6>
                                                                        {/* Line below Cart Summary */}
                                                                        <div className="border-t border-slate-200 dark:border-zink-500 mb-1"></div>
                                    {/* Cart Items List */}
                                    <div className="mb-4 border-b border-slate-200 dark:border-zink-500 flex-1 flex flex-col min-h-0 pb-2">
                                        {/* <h6 className="text-sm font-semibold mb-3">Cart Items</h6> */}
                                        {cartItems.length === 0 ? (
                                            <div className="text-center py-8 text-slate-500 text-sm">
                                                <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                                                <p>Cart is empty</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
                                                {cartItems.map((item) => {
                                                    const unitPrice = parseFloat(item?.unitPrice || '0');
                                                    const discountValue = parseFloat(item?.discountValue || '0');
                                                    const netPrice = unitPrice - discountValue;
                                                    const totalAmount = netPrice * (item?.quantity || 0) * (item?.packOf || 0);
                                                    
                                                    // Get product image from products list if not in cart item
                                                    const cartItemWithImage = item as any;
                                                    const productFromList = products.find(p => p.variantId === item?.variantId);
                                                    const productImage = cartItemWithImage?.imageUrl || productFromList?.imageUrl;
                                                    
                                                    return (
                                                        <div
                                                            key={`cart-item-${item?.variantId}`}
                                                            className="bg-white dark:bg-zink-700 rounded-lg p-2 shadow-sm"
                                                        >
                                                            <div className="flex items-start gap-2 mb-2">
                                                                {/* Product Image */}
                                                                <div className="flex-shrink-0 w-12 h-12 rounded-md bg-slate-100 dark:bg-zink-600 flex items-center justify-center overflow-hidden">
                                                                    {productImage ? (
                                                                        <img
                                                                            src={getImageUrl(productImage)}
                                                                            alt={item?.productName || 'Product'}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    ) : (
                                                                        // <Package className="h-6 w-6 text-slate-400" />
                                                                        <img
                                                                        src={"https://imgdb.plentima.com/arolisa/products/t/95a4c4b3-ad2e-4188-9f20-d4fcfdef6fba.webp"}
                                                                        alt={item?.productName}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 min-w-0 flex-grow">
                                                                    <div className="flex items-start justify-between">
                                                                        <div className="flex-1 min-w-0">
                                                                            <h6 className="text-xs font-medium truncate mb-1">
                                                                                {item?.productName || 'N/A'}
                                                                            </h6>
                                                                            <span className="text-xs text-slate-500">
                                                                                {item?.sku}
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            className="btn btn-xs bg-red-500 text-white p-1 flex-shrink-0 ml-2"
                                                                            onClick={() => removeFromCart(item?.variantId || 0)}
                                                                        >
                                                                            <Trash2 className="h-3 w-3" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <div className="flex items-center gap-1">
                                                                    <button
                                                                        className="btn btn-xs bg-slate-200 hover:bg-slate-300 p-0.5"
                                                                        onClick={() => updateQuantity(item?.variantId || 0, false)}
                                                                    >
                                                                        <Minus className="h-3 w-3" />
                                                                    </button>
                                                                    <span className="font-medium min-w-[1.5rem] text-center">
                                                                        {item?.quantity || 0}
                                                                    </span>
                                                                    <button
                                                                        className="btn btn-xs bg-slate-200 hover:bg-slate-300 p-0.5"
                                                                        onClick={() => updateQuantity(item?.variantId || 0, true)}
                                                                    >
                                                                        <Plus className="h-3 w-3" />
                                                                    </button>
                                                                </div>
                                                                <span className="font-medium">{CURRENCY}{totalAmount.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-3 flex-shrink-0 mt-auto">
                                        {/* Cart Totals */}
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Items ({cartItems.length === 0 ? 0 : (cartSummary?.totalProducts ?? localSummary.totalProducts)})</span>
                                                <span className="font-medium">{CURRENCY}{cartItems.length === 0 ? '0.00' : (cartSummary?.subTotal ?? localSummary.subTotal).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Item Discounts</span>
                                                <span className="text-green-500">-{CURRENCY}{cartItems.length === 0 ? '0.00' : (cartSummary?.totalDiscount ?? localSummary.totalDiscount).toFixed(2)}</span>
                                            </div>
                                            {checkoutStep >= 2 && (
                                                <>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-slate-500">Tax</span>
                                                        <span>{CURRENCY}{cartItems.length === 0 ? '0.00' : (cartSummary?.gst ?? localSummary.gst).toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-slate-500">Shipping</span>
                                                        <span>{CURRENCY}{cartItems.length === 0 ? '0.00' : (checkoutStep === 3 ? 0 : cartSummary?.shipping ?? localSummary.shipping).toFixed(2)}</span>
                                                    </div>
                                                </>
                                            )}
                                            <div className="border-t border-slate-200 dark:border-zink-500 pt-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium">{checkoutStep >= 2 ? 'Total Amount' : 'Subtotal'}</span>
                                                    <span className="text-lg font-semibold">{CURRENCY}{(() => {
                                                        if (cartItems.length === 0) return '0.00';
                                                        if (checkoutStep === 3) return 0;
                                                        if (checkoutStep === 1) {
                                                            // When checkoutStep === 1, exclude shipping and GST from total
                                                            const subtotal = cartSummary?.subTotal ?? localSummary.subTotal;
                                                            const discount = cartSummary?.totalDiscount ?? localSummary.totalDiscount;
                                                            return (subtotal - discount).toFixed(2);
                                                        }
                                                        return (cartSummary?.total ?? localSummary.total).toFixed(2);
                                                    })()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="mt-4 space-y-2">
                                            {checkoutStep === 3 ? (
                                                <button
                                                    className="btn bg-custom-500 text-white w-full py-2"
                                                    onClick={handleNewOrder}
                                                >
                                                    Place New Order
                                                </button>
                                            ) : (
                                                <div className="flex gap-2">
                                                    {checkoutStep > 1 && (
                                                        <button
                                                            className="btn bg-slate-200 hover:bg-slate-300 text-slate-700 flex-1 py-2"
                                                            onClick={handlePrevStep}
                                                        >
                                                            Previous
                                                        </button>
                                                    )}
                                                    {checkoutStep === 2 ? (
                                                        <button
                                                            className="btn bg-green-500 text-white flex-1 py-2"
                                                            onClick={handleSubmitOrder}
                                                            disabled={isSubmitting || !selectedAddress}
                                                        >
                                                            {isSubmitting ? (
                                                                <div className="flex items-center justify-center whitespace-nowrap">
                                                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                                    Processing...
                                                                </div>
                                                            ) : (
                                                                'Submit Order'
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn bg-custom-500 text-white flex-1 py-2"
                                                            onClick={handleNextStep}
                                                            disabled={isNextLoading}
                                                        >
                                                            {isNextLoading ? (
                                                                <div className="flex items-center justify-center whitespace-nowrap">
                                                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                                    Processing...
                                                                </div>
                                                            ) : (
                                                                'Checkout'
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* User Modal */}
            <Modal
                show={showUserModal}
                onHide={toggleUserModal}
                id="userModal"
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[45rem] md:h-[40rem] lg:h-[40rem] bg-white shadow rounded-md dark:bg-zink-600"
            >
                {userData ? (
                    <AddUser onSave={handleUserSave} onHide={toggleUserModal} data={userData} onBusinessSave={handleBusinessSave} isFromPOS={true} />
                ) : (
                    <></>
                )}
            </Modal>
        </React.Fragment>
    );
};

export default POSNew; 